import { TRANSPORTABLE_ROLES_BY_MEDIA } from '../referenceRoleValues.js';
import { isDirectiveMediaPromptTagFormat } from '../../utils/mediaPromptTag.js';
import { formatModelPricing, normalizeModelPricing } from './pricing.js';

export const ModelFamily = Object.freeze({
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  THREE_D: '3d',
});

export const ModelTopology = Object.freeze({
  TEXT_TO_IMAGE: 'text-to-image',
  IMAGE_TO_IMAGE: 'image-to-image',
  IMAGE_TO_DEPTH: 'image-to-depth',
  MASK_IMAGE_TO_IMAGE: 'mask-image-to-image',
  TEXT_TO_VIDEO: 'text-to-video',
  IMAGE_TO_VIDEO: 'image-to-video',
  VIDEO_TO_VIDEO: 'video-to-video',
  VIDEO_EXTEND: 'video-extend',
  TEXT_TO_THREE_D: 'text-to-3d',
  IMAGE_TO_THREE_D: 'image-to-3d',
  THREE_D_TO_THREE_D: '3d-to-3d',
  TEXT_TO_AUDIO: 'text-to-audio',
  AUDIO_TO_AUDIO: 'audio-to-audio',
  AUDIO_TO_TEXT: 'audio-to-text',
  AUDIO_TO_VIDEO: 'audio-to-video',
});

export const ModelSurface = Object.freeze({
  IMAGE_GENERATE: 'image-generate',
  IMAGE_EDIT: 'image-edit',
  IMAGE_INPAINT: 'image-inpaint',
  IMAGE_PREPROCESS: 'image-preprocess',
  VIDEO_GENERATE: 'video-generate',
  VIDEO_RESTYLE: 'video-restyle',
  VIDEO_SMART_EDIT: 'video-smart-edit',
  VIDEO_EXTEND: 'video-extend',
  VIDEO_RELIGHT: 'video-relight',
  VIDEO_RECAST: 'video-recast',
  THREE_D_GENERATE: '3d-generate',
  THREE_D_EDIT: '3d-edit',
  AUDIO_GENERATE: 'audio-generate',
  AUDIO_PROCESS: 'audio-process',
});

export const ReferenceTransportStrategy = Object.freeze({
  NONE: 'none',
  FLAT: 'flat',
  ORDERED_BUCKETS: 'ordered-buckets',
  PROMPT_TAGS: 'prompt-tags',
  NATIVE_SLOTS: 'native-slots',
  NATIVE_ELEMENTS: 'native-elements',
  SOURCE_SLOT: 'source-slot',
});

/**
 * What a route's reference IMAGES mean to the provider.
 *
 * IDENTITY  the image shows a subject, style, or place the model should hold
 *           across the clip. Every reference-to-video route is this.
 * KEYFRAMES the image IS a frame of the output clip, pinned in time and
 *           interpolated through. FLUX 3's reference route is
 *           `first-last-frame-to-video`: the images are spread evenly across
 *           the duration.
 *
 * The distinction is load-bearing in two places and was implicit in neither.
 * A mentioned element used to be forced onto ANY route with image slots, so
 * naming a character on FLUX 3 pushed four photographs of them in as keyframes
 * and the clip interpolated between headshots. Attribution has the same
 * problem from the other side: "use these for identity and appearance" is a
 * false statement about a keyframe.
 */
export const ReferenceImageSemantics = Object.freeze({
  IDENTITY: 'identity',
  KEYFRAMES: 'keyframes',
});

export const UnsupportedReferenceBehavior = Object.freeze({
  TEXT_ONLY: 'text-only',
  WARN: 'warn',
  BLOCK: 'block',
});

export const ModelRouteAdapter = Object.freeze({
  AUDIO_TEXT_MUSIC: 'audio.text.music',
  AUDIO_TEXT_DIALOGUE: 'audio.text.dialogue',
  AUDIO_TEXT_SFX: 'audio.text.sfx',
  AUDIO_TEXT_VOICE: 'audio.text.voice',
  AUDIO_SOURCE_DUBBING: 'audio.source.dubbing',
  AUDIO_SOURCE_TRANSCRIBE: 'audio.source.transcribe',
  AUDIO_SOURCE_VOICE_CHANGE: 'audio.source.voice-change',
  AUDIO_SOURCE_ISOLATION: 'audio.source.isolation',
  AUDIO_SOURCE_STEMS: 'audio.source.stems',
  AUDIO_SOURCE_OUTPAINT: 'audio.source.outpaint',
  AUDIO_SOURCE_RESTYLE: 'audio.source.restyle',
  VIDEO_SOURCE_AUDIO_SYNC: 'video.source.audio-sync',
  IMAGE_EDIT_BUCKETED: 'image.edit.bucketed',
  IMAGE_EDIT_DIMENSIONS: 'image.edit.dimensions',
  IMAGE_EDIT_FLAT: 'image.edit.flat',
  IMAGE_INPAINT_MASK: 'image.inpaint.mask',
  IMAGE_PREPROCESS_DEPTH: 'image.preprocess.depth',
  IMAGE_REFERENCE_PROMPT_TAGS: 'image.reference.prompt-tags',
  IMAGE_REFERENCE_SINGLE_CHARACTER: 'image.reference.single-character',
  IMAGE_REFERENCE_SINGLE_CHARACTER_REMIX: 'image.reference.single-character-remix',
  IMAGE_SMART_EDIT_SOURCE: 'image.smart-edit.source',
  IMAGE_TEXT_BASIC: 'image.text.basic',
  IMAGE_TEXT_DIMENSIONS: 'image.text.dimensions',
  IMAGE_TEXT_RESOLUTION: 'image.text.resolution',
  THREE_D_IMAGE_MULTI_SOURCE: '3d.image.multi-source',
  THREE_D_IMAGE_PROMPT_OPTIONAL: '3d.image.prompt-optional',
  THREE_D_IMAGE_SOURCE: '3d.image.source',
  THREE_D_REMESH_SOURCE_MODEL: '3d.remesh.source-model',
  THREE_D_RETEXTURE_PROMPT: '3d.retexture.prompt',
  THREE_D_RETEXTURE_REFERENCE_IMAGE: '3d.retexture.reference-image',
  THREE_D_TEXT_PROMPT: '3d.text.prompt',
  VIDEO_IMAGE_FIRST_LAST_FRAME: 'video.image.first-last-frame',
  VIDEO_IMAGE_NATIVE_ELEMENTS: 'video.image.native-elements',
  VIDEO_IMAGE_SEQUENCE: 'video.image.sequence',
  VIDEO_IMAGE_SINGLE: 'video.image.single',
  VIDEO_REFERENCE_TO_VIDEO_PROMPT_TAGS: 'video.reference-to-video.prompt-tags',
  VIDEO_RESTYLE_REFERENCES: 'video.restyle.references',
  VIDEO_RESTYLE_SOURCE_VIDEO: 'video.restyle.source-video',
  VIDEO_SMART_EDIT_SOURCE_VIDEO: 'video.smart-edit.source-video',
  VIDEO_TEXT_NATIVE_ELEMENTS: 'video.text.native-elements',
  VIDEO_TEXT_SEQUENCE: 'video.text.sequence',
  VIDEO_TEXT_SINGLE: 'video.text.single',
  VIDEO_EXTEND_SOURCE_VIDEO: 'video.extend.source-video',
  VIDEO_RELIGHT_SOURCE_VIDEO: 'video.relight.source-video',
  VIDEO_RECAST_SOURCE_VIDEO: 'video.recast.source-video',
  VIDEO_SCENE_3D_TO_VIDEO: 'video.scene-3d-to-video',
});

export const DEFAULT_REFERENCE_CONTRACT = Object.freeze({
  acceptedKinds: [],
  acceptedMediaTypes: [],
  roles: [],
  rolesByMedia: Object.freeze({}),
  chips: [],
  limits: Object.freeze({ total: 0 }),
  constraints: Object.freeze({}),
  // Video-accepting PROMPT_TAGS routes declare followSourceAspectRatio explicitly.
  // It is a transport value (adaptive/auto), never a user-selectable aspect enum.
  transport: Object.freeze({ strategy: ReferenceTransportStrategy.NONE }),
  unsupportedBehavior: UnsupportedReferenceBehavior.TEXT_ONLY,
});

const VALID_FAMILIES = new Set(Object.values(ModelFamily));
const VALID_TOPOLOGIES = new Set(Object.values(ModelTopology));
const VALID_SURFACES = new Set(Object.values(ModelSurface));
const VALID_ROUTE_ADAPTERS = new Set(Object.values(ModelRouteAdapter));
const PROMPT_WORKFLOW_FIELDS = new Set([
  'enhancerId',
  'editEnhancerId',
  'restyleEnhancerId',
  'sequenceEnhancerId',
]);

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) {
    return value;
  }

  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

function uniqueStrings(values = []) {
  const seen = new Set();
  const result = [];

  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }

  return result;
}

/**
 * Reference role capability follows the transport, and is never hand-authored.
 *
 * A directive-tag transport compiles roles into prompt sentences, so it can
 * carry the full vocabulary for each media type it accepts. Every other
 * transport carries position (native slots), identity (native elements) or an
 * anonymous index (indexed tags) — none of which express a role. Those get no
 * role choice at all.
 *
 * ⚠️ This is deliberately still keyed on the seedance dialect, not on the
 * PROMPT_TAGS strategy, even though attribution rendering was generalized off
 * the dialect. Widening it here was tried and reverted in the same change: it
 * grants roles to the indexed and spaced dialects, which renames the reference
 * CHIP LABELS a user sees from "Image 1" to "Subject 1" and pulls image models
 * such as kling-o1-image into role declaration. That is a separate product
 * decision about what the picker offers, not a consequence of how the prompt
 * addresses media, and element attribution does not depend on it -- elements
 * carry their own effectiveRole from the element builder.
 *
 * Adding a video model therefore requires no role authoring: declare the
 * transport the provider actually uses and capability follows.
 */
function deriveRolesByMedia({ transport, acceptedMediaTypes }) {
  // Keyed on "is this a directive-tag format", not on one specific literal.
  // Comparing against a single format stripped the role vocabulary from any
  // model that addresses its references with different syntax.
  if (!isDirectiveMediaPromptTagFormat(transport?.promptTagFormat)) return {};

  return Object.fromEntries(
    acceptedMediaTypes
      .filter((mediaType) => TRANSPORTABLE_ROLES_BY_MEDIA[mediaType])
      .map((mediaType) => [mediaType, [...TRANSPORTABLE_ROLES_BY_MEDIA[mediaType]]]),
  );
}

/**
 * An explicit declaration may narrow what the transport supports; it can never
 * widen it. Widening is made structurally impossible rather than merely
 * discouraged, so a config typo cannot re-open the fail-open hole.
 */
function resolveRolesByMedia(derived, override) {
  if (!override || typeof override !== 'object') return derived;

  return Object.fromEntries(
    Object.entries(derived).map(([mediaType, roles]) => {
      // An ABSENT key means "no opinion" — take the derived set. An explicitly
      // declared list narrows it, including an explicit [] meaning "none". The
      // two must stay distinguishable or narrowing to nothing would silently
      // re-open the full vocabulary.
      if (!Object.hasOwn(override, mediaType)) return [mediaType, roles];
      const requested = uniqueStrings(override[mediaType]);
      return [mediaType, requested.filter((role) => roles.includes(role))];
    }),
  );
}

function normalizeReferenceContract(referenceContract = {}) {
  const input = referenceContract && typeof referenceContract === 'object'
    ? referenceContract
    : {};

  const acceptedMediaTypes = uniqueStrings(input.acceptedMediaTypes);
  const transport = {
    ...DEFAULT_REFERENCE_CONTRACT.transport,
    ...(input.transport || {}),
  };

  return {
    ...DEFAULT_REFERENCE_CONTRACT,
    ...input,
    acceptedKinds: uniqueStrings(input.acceptedKinds),
    acceptedMediaTypes,
    roles: uniqueStrings(input.roles),
    rolesByMedia: resolveRolesByMedia(
      deriveRolesByMedia({ transport, acceptedMediaTypes }),
      input.rolesByMedia,
    ),
    chips: uniqueStrings(input.chips),
    limits: {
      ...DEFAULT_REFERENCE_CONTRACT.limits,
      ...(input.limits || {}),
    },
    constraints: {
      ...DEFAULT_REFERENCE_CONTRACT.constraints,
      ...(input.constraints || {}),
    },
    transport,
    unsupportedBehavior: input.unsupportedBehavior
      || DEFAULT_REFERENCE_CONTRACT.unsupportedBehavior,
  };
}

function normalizePositiveNumber(value) {
  const numericValue = Number.parseFloat(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
}

function normalizeTimingProfile(timing = null, owner = 'Model') {
  if (timing == null) return null;
  if (typeof timing !== 'object' || Array.isArray(timing)) {
    throw new Error(`[ModelRepository] ${owner} timing must be an object.`);
  }

  const baseSeconds = normalizePositiveNumber(timing.baseSeconds ?? timing.base);
  if (!baseSeconds) {
    throw new Error(`[ModelRepository] ${owner} timing is missing positive baseSeconds.`);
  }

  return {
    source: 'curated-existing-config',
    ...timing,
    base: baseSeconds,
    baseSeconds,
  };
}

function normalizePromptContract(prompt = null) {
  if (!prompt || typeof prompt !== 'object' || Array.isArray(prompt)) return null;

  const workflowKeys = uniqueStrings([
    ...(prompt.workflowKeys || []),
    ...Array.from(PROMPT_WORKFLOW_FIELDS)
      .map((key) => prompt[key])
      .filter((value) => typeof value === 'string'),
  ]);

  return {
    ...prompt,
    ...(workflowKeys.length > 0 ? { workflowKeys } : {}),
  };
}

function normalizeAgentContract(agent = null) {
  if (!agent || typeof agent !== 'object' || Array.isArray(agent)) return null;

  return {
    ...agent,
    exposeAs: uniqueStrings(agent.exposeAs),
    defaultFor: uniqueStrings(agent.defaultFor),
    available: agent.available === undefined ? true : agent.available === true,
    requiresConfirmation: agent.requiresConfirmation === true,
  };
}

function normalizePresentationContract(definition, normalizedBase) {
  const input = definition.presentation && typeof definition.presentation === 'object'
    ? definition.presentation
    : {};
  const label = typeof input.label === 'string' && input.label.trim()
    ? input.label.trim()
    : normalizedBase.label;
  const shortLabel = typeof input.shortLabel === 'string' && input.shortLabel.trim()
    ? input.shortLabel.trim()
    : label;
  const description = typeof input.description === 'string'
    ? input.description
    : normalizedBase.description;

  const presentationPricing = input.pricing === undefined
    ? normalizedBase.pricing
    : normalizeModelPricing(input.pricing, 'Presentation pricing');

  return {
    label,
    shortLabel,
    description,
    iconKey: typeof input.iconKey === 'string' && input.iconKey.trim()
      ? input.iconKey.trim()
      : normalizedBase.iconKey,
    pricing: presentationPricing,
    cost: formatModelPricing(presentationPricing),
    family: normalizedBase.family,
    group: typeof input.group === 'string' && input.group.trim()
      ? input.group.trim()
      : null,
    tags: uniqueStrings(input.tags || normalizedBase.tags),
  };
}

function normalizeRoute(route = {}, modelId, topology) {
  const input = route && typeof route === 'object' ? route : {};

  return {
    ...input,
    features: input.features || {},
    references: input.references
      ? normalizeReferenceContract(input.references)
      : null,
    execution: input.execution || null,
    timing: normalizeTimingProfile(input.timing, `Model "${modelId}" route "${topology}"`),
  };
}

function validateRouteExecution(modelId, topology, route) {
  const endpoint = typeof route?.endpoint === 'string' ? route.endpoint.trim() : '';
  const execution = route?.execution && typeof route.execution === 'object'
    ? route.execution
    : null;

  if (!endpoint) {
    throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" is missing endpoint.`);
  }

  if (!execution) {
    throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" is missing execution.`);
  }

  if (typeof execution.type !== 'string' || !execution.type.trim()) {
    throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing type.`);
  }

  if (typeof execution.adapterId !== 'string' || !execution.adapterId.trim()) {
    throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing adapterId.`);
  }

  if (!VALID_ROUTE_ADAPTERS.has(execution.adapterId)) {
    throw new Error(
      `[ModelRepository] Model "${modelId}" route "${topology}" has unsupported adapterId "${execution.adapterId}".`
    );
  }

  if (execution.type === 'fal-service-method') {
    if (typeof execution.serviceKey !== 'string' || !execution.serviceKey.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing serviceKey.`);
    }
    if (typeof execution.serviceMethod !== 'string' || !execution.serviceMethod.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing serviceMethod.`);
    }
  }

  if (execution.type === 'fal-service-export') {
    if (typeof execution.serviceKey !== 'string' || !execution.serviceKey.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing serviceKey.`);
    }
    if (typeof execution.exportName !== 'string' || !execution.exportName.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing exportName.`);
    }
  }

  if (execution.type === 'provider-service-export') {
    if (typeof execution.providerKey !== 'string' || !execution.providerKey.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing providerKey.`);
    }
    if (typeof execution.exportName !== 'string' || !execution.exportName.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing exportName.`);
    }
  }

  if (execution.type === 'fal-endpoint') {
    if (typeof execution.templateId !== 'string' || !execution.templateId.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing templateId.`);
    }
    if (typeof execution.methodName !== 'string' || !execution.methodName.trim()) {
      throw new Error(`[ModelRepository] Model "${modelId}" route "${topology}" execution is missing methodName.`);
    }
  }
}

/**
 * Series membership: which product line a model belongs to, so a picker can show one
 * row per family instead of one row per model.
 *
 * Membership is HAND-AUTHORED and never derived. A shared vendor or label prefix is not
 * evidence of a shared series — "Kling O3" and "Kling Video v3" are one series here only
 * because this repo wires both to the same surfaces and topologies, which is what makes
 * them interchangeable to a user. The label is used to *validate* the version suffix
 * after membership is declared; it never establishes membership.
 *
 * `rank` is hand-set because every computable ordering fails: array order is not recency
 * (`seedream-4.5` is declared before `seedream-5-pro`), version strings do not sort
 * ("O3" vs "V3", "V2" vs "3"), and provider publication dates are bulk-backfilled.
 */
function normalizeSeriesContract(series, modelId, label) {
  if (series === undefined || series === null) return null;

  if (typeof series !== 'object' || Array.isArray(series)) {
    throw new Error(`[ModelRepository] Model "${modelId}" has a non-object series.`);
  }

  const seriesId = typeof series.id === 'string' ? series.id.trim() : '';
  const version = typeof series.version === 'string' ? series.version.trim() : '';

  if (!seriesId) {
    throw new Error(`[ModelRepository] Model "${modelId}" series is missing id.`);
  }
  if (!version) {
    throw new Error(`[ModelRepository] Model "${modelId}" series is missing version.`);
  }
  if (!Number.isFinite(series.rank)) {
    throw new Error(`[ModelRepository] Model "${modelId}" series rank must be a finite number.`);
  }

  // The collapsed row renders "<series name> <version>", so the label must already read
  // that way. Checking it here keeps the row text a property of the label rather than a
  // second stored copy of it that can drift.
  const suffix = ` ${version}`;
  if (!label.endsWith(suffix)) {
    throw new Error(
      `[ModelRepository] Model "${modelId}" label "${label}" must end with its series version " ${version}".`
    );
  }

  // No empty-name guard here on purpose: `label` is already trimmed by the caller, so a
  // label ending in " <version>" always has a non-space character before it. A guard was
  // written, found unreachable under test, and removed rather than left as dead code.
  const name = label.slice(0, -suffix.length).trim();

  return { id: seriesId, version, rank: series.rank, name };
}

export function normalizeModelDefinition(definition) {
  if (!definition || typeof definition !== 'object') {
    throw new Error('[ModelRepository] Model definition must be an object.');
  }

  const id = typeof definition.id === 'string' ? definition.id.trim() : '';
  const label = typeof definition.label === 'string' ? definition.label.trim() : '';
  const family = typeof definition.family === 'string' ? definition.family.trim() : '';
  const topologies = uniqueStrings(definition.topologies);
  const surfaces = uniqueStrings(definition.surfaces);

  if (!id) {
    throw new Error('[ModelRepository] Model definition is missing id.');
  }
  if (!label) {
    throw new Error(`[ModelRepository] Model "${id}" is missing label.`);
  }
  if (!VALID_FAMILIES.has(family)) {
    throw new Error(`[ModelRepository] Model "${id}" has invalid family "${family}".`);
  }
  if (topologies.length === 0) {
    throw new Error(`[ModelRepository] Model "${id}" must declare at least one topology.`);
  }
  if (surfaces.length === 0) {
    throw new Error(`[ModelRepository] Model "${id}" must declare at least one surface.`);
  }

  const invalidTopology = topologies.find((topology) => !VALID_TOPOLOGIES.has(topology));
  if (invalidTopology) {
    throw new Error(`[ModelRepository] Model "${id}" has invalid topology "${invalidTopology}".`);
  }

  const invalidSurface = surfaces.find((surface) => !VALID_SURFACES.has(surface));
  if (invalidSurface) {
    throw new Error(`[ModelRepository] Model "${id}" has invalid surface "${invalidSurface}".`);
  }

  const routes = Object.fromEntries(
    topologies.map((topology) => {
      const route = definition.routes?.[topology];
      validateRouteExecution(id, topology, route);
      return [topology, normalizeRoute(route, id, topology)];
    }),
  );

  const normalizedBase = {
    description: typeof definition.description === 'string' ? definition.description : '',
    iconKey: typeof definition.iconKey === 'string' && definition.iconKey.trim()
      ? definition.iconKey.trim()
      : 'generic-ai',
    pricing: normalizeModelPricing(definition.pricing, `Model "${id}" pricing`),
    tags: uniqueStrings(definition.tags),
    family,
    label,
  };
  const prompt = normalizePromptContract(definition.prompt);
  const agent = normalizeAgentContract(definition.agent);
  const timing = normalizeTimingProfile(definition.timing, `Model "${id}"`);

  if (!timing) {
    throw new Error(`[ModelRepository] Model "${id}" is missing timing.`);
  }

  return deepFreeze({
    status: 'active',
    provider: 'fal',
    features: {},
    verification: null,
    ...definition,
    id,
    label,
    family,
    description: normalizedBase.description,
    iconKey: normalizedBase.iconKey,
    pricing: normalizedBase.pricing,
    cost: formatModelPricing(normalizedBase.pricing),
    tags: normalizedBase.tags,
    topologies,
    surfaces,
    routes,
    prompt,
    agent,
    timing,
    series: normalizeSeriesContract(definition.series, id, label),
    presentation: normalizePresentationContract(definition, normalizedBase),
    references: normalizeReferenceContract(definition.references),
    activation: {
      defaultActive: definition.activation?.defaultActive !== false,
      ...(definition.activation || {}),
    },
  });
}

/**
 * Catalog-wide series checks.
 *
 * The one that earns its place is the >=2 members rule: a mistyped `series.id` splits a
 * family and leaves an orphan, which would otherwise render as an ordinary plain row and
 * hide the typo indefinitely. Everything else here is a straight ambiguity check —
 * duplicate ranks have no defined default face, duplicate versions render two identical
 * expanded rows, and a disagreeing name means the labels do not describe one family.
 */
function validateSeriesGroups(models) {
  const groups = new Map();

  models.forEach((model) => {
    if (!model.series) return;
    if (!groups.has(model.series.id)) groups.set(model.series.id, []);
    groups.get(model.series.id).push(model);
  });

  groups.forEach((members, seriesId) => {
    if (members.length < 2) {
      throw new Error(
        `[ModelRepository] Series "${seriesId}" has only one member ("${members[0].id}"). `
        + 'A one-member series is a mistyped series id or a premature annotation; '
        + 'an ungrouped model should omit series entirely.'
      );
    }

    const families = new Set(members.map((model) => model.family));
    if (families.size > 1) {
      throw new Error(
        `[ModelRepository] Series "${seriesId}" spans multiple families: ${[...families].sort().join(', ')}.`
      );
    }

    const seenRanks = new Set();
    const seenVersions = new Set();
    members.forEach((model) => {
      if (seenRanks.has(model.series.rank)) {
        throw new Error(
          `[ModelRepository] Series "${seriesId}" has duplicate rank ${model.series.rank}; ranks decide the default face and must be unique.`
        );
      }
      seenRanks.add(model.series.rank);

      if (seenVersions.has(model.series.version)) {
        throw new Error(
          `[ModelRepository] Series "${seriesId}" has duplicate version "${model.series.version}".`
        );
      }
      seenVersions.add(model.series.version);
    });

    const names = new Set(members.map((model) => model.series.name));
    if (names.size > 1) {
      throw new Error(
        `[ModelRepository] Series "${seriesId}" derives conflicting names from its labels: ${[...names].sort().map((n) => `"${n}"`).join(', ')}.`
      );
    }
  });
}

export function normalizeModelRepository(definitions = []) {
  const seen = new Set();
  const models = definitions.map((definition) => {
    const model = normalizeModelDefinition(definition);
    if (seen.has(model.id)) {
      throw new Error(`[ModelRepository] Duplicate model id "${model.id}".`);
    }
    seen.add(model.id);
    return model;
  });

  validateSeriesGroups(models);

  return deepFreeze(models);
}

export function mergeModelContracts(base = {}, override = {}) {
  const merged = {
    ...base,
    ...override,
  };

  if (base.limits || override.limits) {
    merged.limits = {
      ...(base.limits || {}),
      ...(override.limits || {}),
    };
  }

  if (base.transport || override.transport) {
    merged.transport = {
      ...(base.transport || {}),
      ...(override.transport || {}),
    };
  }

  if (base.rolesByMedia || override.rolesByMedia) {
    merged.rolesByMedia = {
      ...(base.rolesByMedia || {}),
      ...(override.rolesByMedia || {}),
    };
  }

  return merged;
}
